import { FC, useState, ChangeEvent } from 'react';
import ModalWindow from '../ModalWindow';
import OrderApi from 'src/api/OrderApi';
import useOrder from 'src/hooks/useOrder';
import { Button, Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ExcelUploadResult } from 'src/types/excelUploadResult';
import { ExcelOrderResponse } from 'src/types/excelOrderResponse';
import { CircularProgress, Grid } from '@mui/material';
import '../../theme/partials/ExcelUploadModal.css';
import useOrderSteps from 'src/hooks/useOrderSteps';
import { useNavigate } from 'react-router';

type Props = {
  open: boolean;
  onClose?: () => void;
  isExcelOrder?: boolean;
  title?: string;
};

const ExcelUploadModal: FC<React.PropsWithChildren<Props>> = ({ open, onClose, title = 'Upload Excel Item List', isExcelOrder = false }) => {
  const { order } = useOrder();
  const [results, setResults] = useState<ExcelUploadResult>();
  const [orderResults, setOrderResults] = useState<ExcelOrderResponse>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [filename, setFilename] = useState('');
  const baseApiUrl = import.meta.env.REACT_APP_SERVICE_URL;
  const { setCurrentOrderStep } = useOrderSteps();
  const navigate = useNavigate();

  const handleExcelItemUpload: () => void = async () => {
    if (results || error) {
      onClose();
      setFilename('');
      setResults(null);
      setError(null);
      window.location.reload();
      return;
    }

    setIsLoading(true);
    var form = document.getElementById('upload-form') as HTMLFormElement;
    var formData = new FormData(form);
    var apiResult = await OrderApi.uploadOrderItems(order.sfaOrderId, formData).catch((err) => setError(err));

    if (apiResult) {
      setResults(apiResult);
    }
    setIsLoading(false);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    const { name } = file;
    setFilename(name);
  };

  const handleExcelOrderUpload: () => void = async () => {
    setOrderResults(null);
    setIsLoading(true);
    var form = document.getElementById('upload-form') as HTMLFormElement;
    var formData = new FormData(form);

    await OrderApi.uploadOrder(formData)
      .then((res) => {
        if (res.isSuccess) {
          onClose();
          setFilename('');
          setCurrentOrderStep(4);
          navigate(`/${res.customerNumber}/OrderSubmit/${res.sfaOrderId}`);
        } else {
          setOrderResults(res);
        }
      })
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  };

  return (
    <ModalWindow
      key="UploadExcelModal"
      open={open}
      title={title}
      hasCancel={!results}
      centerAlignTitle={results ? true : false}
      hasBtnDivider={true}
      onCancel={() => {
        onClose();
        setFilename('');
        setOrderResults(null);
      }}
      onOK={isExcelOrder ? handleExcelOrderUpload : handleExcelItemUpload}
      isLoading={isLoading}
    >
      <Box sx={{ minWidth: 250 }}>
        <form className="inline-form" id="upload-form">
          {!results && !error && (
            <div>
              <Box className="upload-form__btn-file">
                <Button variant="contained" component="label" sx={{ mr: 1 }}>
                  Upload Excel File
                  <input
                    hidden
                    type="file"
                    id="file"
                    name="file"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.ms-excel.sheet.macroEnabled.12"
                    onChange={handleFileUpload}
                  />
                </Button>
                {filename && <Box className="file-result-text">{filename}</Box>}
              </Box>
              {!isExcelOrder && (
                <Box sx={{ mt: 3 }}>
                  <ul style={{ marginTop: 0 }}>
                    <li>Data must be on the first sheet in the workbook</li>
                    <li>First column must contain the item number</li>
                    <li>Second column must contain the quantity</li>
                    <li>First row is assumed to be header and will be skipped</li>
                  </ul>
                  <a download={true} className="download-link" href='/ItemUploadTemplate_v2.xlsx'>
                    {' '}
                    <DownloadIcon style={{ marginRight: '0.1em' }} /> Download Excel Template
                  </a>
                </Box>
              )}
              {isLoading && (
                <>
                  <br />
                  <CircularProgress sx={{ ml: 17 }} />
                </>
              )}
              {isExcelOrder && !orderResults?.isSuccess && orderResults != null && (
                <>
                  <p>{orderResults?.message}</p>
                  {orderResults?.errors.length > 0 && (
                    <ul>
                      {orderResults?.errors?.map((e, i) => (
                        <li key={i}>
                          <Grid size={{ xs: 12, md: 8 }}>{e}</Grid>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
          {results && (
            <div>
              {results.allSuccess && <p style={{ margin: '0 20px 20px', textAlign: 'center' }}>File uploaded succesfully.</p>}
              {results.anyQuantitiesChanged && <p>Some quanitities changed due to sell multiples.</p>}
              {!results.allSuccess && <p>At least one item failed to upload.</p>}

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="contained" startIcon={<DownloadIcon />} href={`${baseApiUrl}/OrderEntry/UploadOrderItems/Result?fileName=${results.fileName}`}>
                  Download Results
                </Button>
              </Box>
            </div>
          )}
          {error && <p>An error occurred: {error}. Please hit OK and try again.</p>}
        </form>
      </Box>
    </ModalWindow>
  );
};

export default ExcelUploadModal;
